import React, { useEffect, useState, useCallback, useMemo } from "react";
import "../common/Util.css";
import "../common/skeleton-effect.css";
import "./trip-live-track.css";
import TruckProgressBar from "./truck-progress-bar";
import { useNavigate, useLocation } from "react-router-dom";
import { Toast, ToastContainer } from "react-bootstrap";
import { MoveLeft } from "lucide-react";
import CommonDropdown from "../common/common_dropDown";
import { getStopLocationsApi, getDeliveryLocationsApi, getTripHistoryRouteApi, getTripPlanRouteApi, getStopsListApi, fetchActualRouteApi } from "./live-view-api";
import polyline from "@mapbox/polyline";
import stopPinImg from "../../assets/png/planned-pin.png";
import { MapContainer, TileLayer, Marker, Popup, Polyline } from "react-leaflet";
import L from "leaflet";
import tripRouteIcon from "../../assets/svg/trip_route_ic1.svg";
import receiverIcon from "../../assets/svg/receiver_green_ic.svg";
import locationIcon from "../../assets/svg/location_pin_red.svg";
import stopIcon from "../../assets/svg/stops-inactive.svg";
import minutesIC from "../../assets/svg/minutes_ic.svg";
import completedPinImg from "../../assets/png/delivery-pin.png";

import { useGlobal } from "../../GlobalContext.js";
import MarkerClusterGroup from "react-leaflet-markercluster";
import ProfileImageModal from "../common/ProfileImageModal";
import { sanitizeInput, sanitizeNumericInput, validateUrl } from "../common/security-utils";

export const TripLiveTrack = () => {
  const { rolePermissionList } = useGlobal();

  const checkPermission = useCallback((menuName, permission) => {
    if (!rolePermissionList || !Array.isArray(rolePermissionList)) return false;
    return rolePermissionList.findIndex((element) => element?.["name"] === menuName && element?.["menu_permission"] === true && element[permission] === true) > -1;
  }, [rolePermissionList]);

  const apiUrl = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();
  const location = useLocation();

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingRoute, setIsLoadingRoute] = useState(false);
  const [isLoadingStops, setIsLoadingStops] = useState(false);
  const [trip, setTrip] = useState({});
  const [stopsFilter, setStopsFilter] = useState("All");
  const [query, setQuery] = useState("");
  const [tripOption, setTripOption] = useState(null);
  const [stopPinLocation, setStopPinLocation] = useState([]);
  const [deliveryPinLocation, setDeliveryPinLocation] = useState([]);
  const [polylinePlanned, setPolylinePlanned] = useState([]);
  const [polylineHistory, setPolylineHistory] = useState([]);
  const [stopList, setStopList] = useState([]);
  const [tripId, setTripId] = useState(null);
  const [toast, setToast] = useState({
    show: false,
    message: "",
    variant: "success",
  });
  const [showProfilePopup, setShowProfilePopup] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState("");
  const { id, tripCode } = location.state || {};

  const createPolylines = useCallback((routeList, type) => {
    const newPolylines = [];
    const newDeliveryPins = [];

    routeList.forEach((item, index) => {
      if (item?.points && typeof item.points === "string" && item.points.length > 0) {
        try {
          const decoded = polyline.decode(item.points);

          if (decoded && decoded.length > 0) {
            if (type === "history" && item.plot === true) {
              newDeliveryPins.push({
                latitude: decoded[0][0],
                longitude: decoded[0][1],
                stop_code: sanitizeInput(item.stop_code || ""),
                trip_id: sanitizeInput(item.trip_id || ""),
                status: sanitizeInput(item.status || "")
              });
            }

            newPolylines.push({
              id: `poly_${type}_${index}_${Date.now()}`,
              positionList: decoded,
            });
          }
        } catch (parseError) {
          // Internal error logging only
        }
      }
    });

    if (type === "history") {
      setPolylineHistory(newPolylines);
      if (newDeliveryPins.length > 0) {
        setDeliveryPinLocation(newDeliveryPins);
      }
    } else {
      setPolylinePlanned(newPolylines);
    }
  }, []);

  const getStopLocations = useCallback(async (tId) => {
    setIsLoading(true);
    try {
      const response = await getStopLocationsApi(tId);

      if (response?.data?.success) {
        const tripDetails = response.data.trip ?? {};
        setTrip(tripDetails);

        if (tripDetails["stopadderss"]?.length > 0) {
          const validStops = tripDetails["stopadderss"]
            .filter((marker) => marker["latitude"] && marker["longitude"])
            .map(marker => ({
              ...marker,
              latitude: marker.latitude,
              longitude: marker.longitude,
              stop_code: sanitizeInput(marker.stop_code || ""),
              address: sanitizeInput(marker.address || ""),
              customer: sanitizeInput(marker.customer || "")
            }));
          setStopPinLocation(validStops);
        }
      }
    } catch (err) {
      // Silent error for security
    } finally {
      setIsLoading(false);
    }
  }, []);

  const getDeliveryLocations = useCallback(async (tId) => {
    try {
      const response = await getDeliveryLocationsApi(tId);

      if (response?.data?.success) {
        const tripDetails = response.data.data ?? [];

        if (tripDetails.length > 0) {
          const validDeliveries = tripDetails
            .filter((marker) => marker["status"] === "Delivered" && marker["latitude"] && marker["longitude"])
            .map(marker => ({
              ...marker,
              latitude: marker.latitude,
              longitude: marker.longitude,
              stop_code: sanitizeInput(marker.stop_code || ""),
              trip_id: sanitizeInput(marker.trip_id || ""),
              status: sanitizeInput(marker.status || "")
            }));
          setDeliveryPinLocation(validDeliveries);
        }
      }
    } catch (err) {
      // Silent error
    }
  }, []);

  const getStopsList = useCallback(async (tId) => {
    setIsLoadingStops(true);
    try {
      const response = await getStopsListApi(tId, stopsFilter, query);

      if (response?.data?.success) {
        const sanitizedStops = (response.data.stops ?? []).map(stop => ({
          ...stop,
          stop_code: sanitizeInput(stop.stop_code || ""),
          customer: sanitizeInput(stop.customer || ""),
          address: sanitizeInput(stop.address || ""),
          ph_number: sanitizeInput(stop.ph_number || ""),
          planned_time: sanitizeInput(stop.planned_time || ""),
          actual_time: sanitizeInput(stop.actual_time || "")
        }));
        setStopList(sanitizedStops);
      }
    } catch (err) {
      // Silent error
    } finally {
      setIsLoadingStops(false);
    }
  }, [stopsFilter, query]);

  const getTripPlanRoute = useCallback(async (tId) => {
    try {
      const response = await getTripPlanRouteApi(tId);

      if (response?.data?.success) {
        const routeList = response.data.response ?? [];
        if (routeList.length > 0) {
          createPolylines(routeList, "customer");
        }
      }
    } catch (err) {
      // Silent error
    }
  }, [createPolylines]);

  const getTripHistoryRoute = useCallback(async (tId, retry = false) => {
    try {
      setIsLoadingRoute(true);
      const response = await getTripHistoryRouteApi(tId);

      if (response?.data?.success) {
        const routeList = response.data.response ?? [];

        if (routeList.length > 0) {
          createPolylines(routeList, "history");
        } else {
          if (retry) {
            setToast({
              show: true,
              message: "Actual route not found",
              variant: "danger",
            });
            return;
          }

          const actualRouteResponse = await fetchActualRouteApi(tId);

          if (actualRouteResponse?.data?.success) {
            await getTripHistoryRoute(tId, true);
          } else {
            setToast({
              show: true,
              message: "Actual route not found",
              variant: "danger",
            });
          }
        }
      }
    } catch (err) {
      // Silent error
    } finally {
      setIsLoadingRoute(false);
    }
  }, [createPolylines]);

  useEffect(() => {
    if (id != null) {
      setTripId(id);
      setTripOption({ value: id, label: tripCode });
      getStopLocations(id);
      getDeliveryLocations(id);
      getStopsList(id);
      getTripPlanRoute(id);
    }
  }, [id, tripCode, getStopLocations, getDeliveryLocations, getStopsList, getTripPlanRoute]);

  useEffect(() => {
    if (tripId != null) {
      getStopsList(tripId);
    }
  }, [tripId, stopsFilter, query, getStopsList]);

  const createCustomMarker = useCallback((text) =>
    L.divIcon({
      className: "custom-icon",
      html: `
            <div style="position: relative; display: inline-block;">
              <img src="${stopPinImg}" style="width:40px;height:40px;" />
              <span style="
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                color: white;
                font-size: 14px;
                font-weight: bold;
              ">
                ${sanitizeInput(text.toString())}
              </span>
            </div>
          `,
      iconSize: [40, 40],
      iconAnchor: [20, 40],
      popupAnchor: [0, -40],
    }), []);

  const createCompletedPinMarker = useCallback((text) =>
    L.divIcon({
      className: "custom-icon",
      html: `
            <div style="position: relative; display: inline-block;">
              <img src="${completedPinImg}" />
              <span style="
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                color: white;
                font-size: 14px;
                font-weight: bold;
              ">
                ${sanitizeInput(text.toString())}
              </span>
            </div>
          `,
      iconSize: [40, 40],
      iconAnchor: [20, 40],
      popupAnchor: [0, -40],
    }), []);

  return (
    <>
      <div className="bg-half-white pt-2 rounded-4 trip-live-track-container">
        {isLoading ? (
          <>
            {/* Skeleton for top bar */}
            <div className="d-flex align-items-center justify-content-between p-2" style={{ gap: 12 }}>
              <div className="col-12 col-md-6 col-lg-4 d-flex align-items-center gap-3 p-2 trip-top-bar-card">
                <div className="skeleton skeleton-badge-wrapper" />
                <div className="flex-grow-1">
                  <div className="skeleton skeleton-text-sm mb-2" style={{ width: "50px" }} />
                  <div className="skeleton skeleton-text" style={{ width: "100%" }} />
                </div>
                <div className="skeleton skeleton-badge" />
              </div>

              <div className="col-12 col-md-6 col-lg-8 d-flex align-items-center gap-2 p-2 trip-top-bar-card">
                <div className="d-flex w-100 gap-2">
                  <div className="col-6 d-flex gap-2">
                    {Array.from({ length: 3 }).map((_, idx) => (
                      <div key={idx} className="col-4 p-2 rounded-3" style={{ background: "#F8F8F8" }}>
                        <div className="skeleton skeleton-text-sm mb-2" style={{ width: "60px" }} />
                        <div className="skeleton skeleton-text" style={{ width: "40px" }} />
                      </div>
                    ))}
                  </div>
                  <div className="col-6">
                    <div className="w-100 d-flex align-items-center justify-content-between p-2 mx-3 driver-info-card">
                      <div className="d-flex align-items-center gap-2">
                        <div className="skeleton driver-avatar" />
                        <div>
                          <div className="skeleton skeleton-text mb-2" style={{ width: "100px" }} />
                          <div className="skeleton skeleton-text-sm" style={{ width: "80px" }} />
                        </div>
                      </div>
                      <div className="d-flex gap-2">
                        <div className="skeleton skeleton-badge-wrapper" />
                        <div className="skeleton skeleton-badge-wrapper" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Skeleton for timeline section */}
            <div className="bg-white rounded-4 p-2 mt-3">
              <div className="mb-3 d-flex align-items-center justify-content-between">
                <div className="col-5">
                  <div className="skeleton skeleton-text mb-2" style={{ width: "120px" }} />
                  <div className="skeleton skeleton-text-sm mb-2" style={{ width: "150px" }} />
                  <div className="d-flex align-items-center gap-2">
                    <div className="skeleton skeleton-badge" />
                    <div className="skeleton" style={{ width: "20px", height: "8px" }} />
                    <div className="skeleton skeleton-text-sm" style={{ width: "60px" }} />
                  </div>
                </div>
                <div className="col-2">
                  <div className="skeleton skeleton-button" />
                </div>
                <div className="col-5">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <div className="skeleton skeleton-text-sm" style={{ width: "60px" }} />
                    <div className="skeleton skeleton-text-sm" style={{ width: "60px" }} />
                  </div>
                  <div className="skeleton" style={{ width: "100%", height: "8px", borderRadius: "4px" }} />
                </div>
              </div>

              {/* Skeleton for map and stops panel */}
              <div className="d-flex" style={{ gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <div className="skeleton skeleton-map" style={{ height: "68vh", borderRadius: "16px" }} />
                </div>

                <div style={{ width: 380 }}>
                  <div className="bg-white rounded-4 p-3" style={{ height: "68vh", overflowY: "auto" }}>
                    {/* Next stop skeleton */}
                    <div className="rounded-4 p-3 mb-3" style={{ background: "#F8F8F8" }}>
                      <div className="bg-white rounded-3 p-2 d-flex align-items-center justify-content-between mb-3">
                        <div className="d-flex align-items-center gap-2">
                          <div className="skeleton" style={{ width: "28px", height: "28px", borderRadius: "50%" }} />
                          <div className="skeleton skeleton-text" style={{ width: "80px" }} />
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <div className="skeleton skeleton-badge" />
                          <div className="skeleton" style={{ width: "28px", height: "28px", borderRadius: "50%" }} />
                        </div>
                      </div>
                      <div className="d-flex align-items-center gap-2 mb-3">
                        <div className="skeleton" style={{ width: "36px", height: "36px", borderRadius: "8px" }} />
                        <div className="flex-grow-1">
                          <div className="skeleton skeleton-text-sm mb-2" style={{ width: "100px" }} />
                          <div className="skeleton skeleton-text-sm" style={{ width: "80px" }} />
                        </div>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <div className="skeleton" style={{ width: "36px", height: "36px", borderRadius: "50%" }} />
                        <div className="skeleton skeleton-text-sm flex-grow-1" style={{ width: "100%" }} />
                      </div>
                    </div>

                    {/* Filter buttons skeleton */}
                    <div className="d-flex gap-2 mb-3">
                      {Array.from({ length: 3 }).map((_, idx) => (
                        <div key={idx} className="skeleton skeleton-tab" />
                      ))}
                    </div>

                    {/* Search skeleton */}
                    <div className="mb-3">
                      <div className="skeleton" style={{ width: "100%", height: "38px", borderRadius: "8px" }} />
                    </div>

                    {/* Stop cards skeleton */}
                    {Array.from({ length: 3 }).map((_, idx) => (
                      <div key={idx} className="rounded-4 p-3 mb-3" style={{ background: "#F8F8F8" }}>
                        <div className="bg-white rounded-3 p-2 d-flex align-items-center justify-content-between mb-3">
                          <div className="d-flex align-items-center gap-2">
                            <div className="skeleton" style={{ width: "28px", height: "28px", borderRadius: "50%" }} />
                            <div className="skeleton skeleton-text" style={{ width: "80px" }} />
                          </div>
                          <div className="skeleton skeleton-badge" />
                        </div>
                        <div className="d-flex align-items-center gap-2 mb-3">
                          <div className="skeleton" style={{ width: "36px", height: "36px", borderRadius: "8px" }} />
                          <div className="flex-grow-1">
                            <div className="skeleton skeleton-text-sm mb-2" style={{ width: "100px" }} />
                            <div className="skeleton skeleton-text-sm" style={{ width: "80px" }} />
                          </div>
                        </div>
                        <div className="d-flex align-items-center gap-2 mb-3">
                          <div className="skeleton" style={{ width: "36px", height: "36px", borderRadius: "50%" }} />
                          <div className="skeleton skeleton-text-sm flex-grow-1" />
                        </div>
                        <div className="bg-white rounded-3 d-flex gap-2 p-2">
                          <div className="skeleton skeleton-text-sm flex-grow-1" />
                          <div className="skeleton skeleton-text-sm flex-grow-1" />
                          <div className="skeleton skeleton-text-sm" style={{ width: "40px" }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Top bar: Trip selector + summary */}
            <div className="d-flex align-items-center justify-content-between gap-2">
              <div className="col-12 col-md-6 col-lg-4 d-flex align-items-center gap-3 p-2" style={{ borderRadius: "16px", background: "#FFFFFF" }}>
                <button
                  className="btn btn-light rounded-circle back-button"
                  onClick={() => {
                    navigate(-1);
                  }}
                >
                  <MoveLeft className="cursor-pointer" />
                </button>
                <div className="d-flex flex-column align-items-start" style={{ flex: 1 }}>
                  <div className="trip-drop-down">Trip ID</div>
                  <div className="w-100 d-flex align-items-center gap-2 mt-2">
                    <CommonDropdown
                      apiUrl={`${apiUrl}trips/autosuggestion?module=tripcost&fromDate=&toDate=`}
                      placeholder="Select"
                      value={tripOption}
                      onSelect={(item) => {
                        setStopPinLocation([]);
                        setDeliveryPinLocation([]);
                        setPolylinePlanned([]);
                        setPolylineHistory([]);
                        setTripOption(item);
                        getStopLocations(item.value);
                        getDeliveryLocations(item.value);
                        getStopsList(item.value);
                        getTripPlanRoute(item.value);
                        setTripId(item.value);
                      }}
                    />
                  </div>
                </div>
                <div className="badge px-3 py-2 trip-status-badge">
                  {sanitizeInput(trip?.status || "")}
                </div>
              </div>

              <div className="col-12 col-md-6 col-lg-8 d-flex align-items-center gap-3 p-2 trip-summary-card">
                <div className="d-flex w-100 gap-2">
                  <div className="col-6 d-flex gap-2">
                    <div className="col-4 p-2 rounded-3 text-start trip-count-card stops">
                      <div className="trip-count-title">STOPS</div>
                      <div className="trip-count-value mt-2">{sanitizeNumericInput(trip?.total_stops || 0)}</div>
                    </div>
                    <div className="col-4 p-2 rounded-3 text-start trip-count-card completed">
                      <div className="trip-count-title">COMPLETED</div>
                      <div className="trip-count-value mt-2">{sanitizeNumericInput(trip?.completed_stops || 0)}</div>
                    </div>
                    <div className="col-4 p-2 rounded-3 text-start trip-count-card pending">
                      <div className="trip-count-title">PENDING</div>
                      <div className="trip-count-value mt-2">{sanitizeNumericInput(trip?.pending_stops || 0)}</div>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="d-flex align-items-center justify-content-between p-2 driver-info-card">
                      <div className="d-flex align-items-center gap-2">
                        <div className="driver-avatar">
                          {trip.driverDetails?.length > 0 ? sanitizeInput(trip?.driverDetails[0]?.driver_name?.charAt(0) || "D") : "D"}
                        </div>
                        <div className="d-flex flex-column align-items-start ms-1">
                          <div className="fw-700">{trip.driverDetails?.length > 0 ? sanitizeInput(trip.driverDetails[0]?.driver_name || "---") : "---"}</div>
                          <div className="fs-12 text-muted">{sanitizeInput(trip.license_plate || "")}</div>
                        </div>
                      </div>

                      <div className="d-flex gap-2" style={{ width: "86px" }}>
                        <div className="d-flex align-items-center justify-content-center action-icon-wrapper">
                          <img src={tripRouteIcon} alt="Trip Route Icon" />
                        </div>

                        {trip.driverDetails?.length > 0 && (
                          <div
                            onClick={() => {
                              const mobile = trip.driverDetails[0]?.mobile_number;
                              if (mobile) window.location.href = `tel:${sanitizeInput(mobile)}`;
                            }}
                            className="d-flex align-items-center justify-content-center action-icon-wrapper"
                          >
                            <img src={receiverIcon} alt="Receiver Icon" />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Timeline slider (simple visual) */}
            <div className="bg-white rounded-4 mt-2 p-2 timeline-section">
              <div className="mb-3 d-flex align-items-center justify-content-between">
                <div className="col-5">
                  <div className="d-flex flex-column align-items-start">
                    <div className="fs-15 fw-700" style={{ color: "#2F2F2F" }}>
                      {sanitizeInput(trip.license_plate || "")}
                    </div>
                    <div className="fs-13 fw-400" style={{ color: "#3F9A30" }}>
                      Running from <span>{sanitizeInput(trip.vehicle_running_time || "---")}</span>
                    </div>
                    <div className="mt-1 d-flex align-items-center justify-content-start">
                      {trip.vehicle_status === true && (
                        <div className="px-3 py-1 rounded-4 d-flex align-items-center justify-content-center" style={{ background: "#3F9A30" }}>
                          <span className="fs-13 fw-700 text-white">On</span>
                        </div>
                      )}
                      <span className="mx-2 px-1 py-1 rounded-4 " style={{ background: "#A7B1BB" }}></span>
                      <img src={minutesIC} alt="minutes icon" />
                      <span className="ms-1 fs-11 fw-600" style={{ color: "#95A1AD" }}>
                        {sanitizeInput(trip?.vehicle_start_time || "")}
                      </span>
                    </div>
                  </div>
                </div>
                {checkPermission("Live view", "menu_permission") && (
                  <div className="col-2 d-flex align-items-center justify-content-center">
                    <button
                      className="btn btn-primary btn-sm d-flex align-items-center justify-content-center gap-2 actual-route-btn"
                      onClick={() => getTripHistoryRoute(tripId)}
                      disabled={isLoadingRoute}
                    >
                      {isLoadingRoute ? (
                        <>
                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                          Loading...
                        </>
                      ) : (
                        "Actual Route"
                      )}
                    </button>
                  </div>
                )}

                <div className="col-5 d-flex flex-column">
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="fs-12 text-muted">{sanitizeInput(trip?.start_date || "")}</div>
                    <div className="fs-12 text-muted">{sanitizeInput(trip?.end_date || "")}</div>
                  </div>
                  <TruckProgressBar
                    totalStops={trip?.total_stops || 0}
                    currentStop={
                      ["trip end", "delivered", "completed"].includes((trip?.status || "").toLowerCase())
                        ? (trip?.total_stops || 0)
                        : (trip?.total_stops || 0) - (trip?.pending_stops || 0)
                    }
                    showLabels={false}
                  />
                </div>

              </div>

              {/* Main content: Map (left) + Stops panel (right) */}
              <div className="d-flex" style={{ gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <div className="bg-white rounded-4 map-container-wrapper">
                    {/* Skeleton overlay when loading route */}
                    {isLoadingRoute && (
                      <div className="loading-overlay">
                        <div className="d-flex flex-column align-items-center gap-3">
                          <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading route...</span>
                          </div>
                          <div className="fs-14 fw-500" style={{ color: "#0C62B8" }}>
                            Loading actual route...
                          </div>
                        </div>
                      </div>
                    )}
                    {/* Map placeholder: simulate route with SVG and markers */}
                    {!trip?.id ? (
                      // Show skeleton when trip data isn't loaded yet
                      <div className="skeleton skeleton-map-wrapper" />
                    ) : (
                      <div className="map-placeholder">
                        <MapContainer center={[20.5937, 78.9629]} zoom={5} style={{ height: "100%", width: "100%" }}>
                          <TileLayer
                            url={process.env.REACT_APP_TILE_URL || "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"}
                            attribution={process.env.REACT_APP_TILE_ATTRIBUTION || '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap</a>'}
                          />

                          {/* Markers for stops from stopList */}
                          {/*  {stopList
                        .filter((stop) => stop.latitude && stop.longitude && stop.latitude !== "" && stop.longitude !== "")
                        .map((stop, index) => {
                          const lat = parseFloat(stop.latitude);
                          const lng = parseFloat(stop.longitude);
                          const icon = stop.status === true ? createCompletedPinMarker(stop.seq || index + 1) : createCustomMarker(stop.seq || index + 1);

                          return (
                            <Marker key={`stop-${stop.stop_code}-${index}`} position={[lat, lng]} icon={icon}>
                              <Popup>
                                <div style={{ minWidth: "250px", padding: "10px" }}>
                                  <h6 style={{ fontWeight: "bold", marginBottom: "8px" }}>#{stop.stop_code}</h6>
                                  <p style={{ margin: "4px 0", fontSize: "13px" }}>
                                    <strong>Customer:</strong> {stop.customer || "N/A"}
                                  </p>
                                  <p style={{ margin: "4px 0", fontSize: "13px" }}>
                                    <strong>Location:</strong> {stop.location_name || "N/A"}
                                  </p>
                                  <p style={{ margin: "4px 0", fontSize: "13px" }}>
                                    <strong>Address:</strong> {stop.address || "N/A"}
                                  </p>
                                  {stop.ph_number && (
                                    <p style={{ margin: "4px 0", fontSize: "13px" }}>
                                      <strong>Phone:</strong> {stop.ph_number}
                                    </p>
                                  )}
                                  <p style={{ margin: "4px 0", fontSize: "13px" }}>
                                    <strong>Status:</strong> <span style={{ color: stop.status ? "#2C8C53" : "#FFC05D", fontWeight: "bold" }}>{stop.status ? "Completed" : "Pending"}</span>
                                  </p>
                                  {stop.planned_time && (
                                    <p style={{ margin: "4px 0", fontSize: "12px" }}>
                                      <strong>Planned:</strong> {stop.planned_time}
                                    </p>
                                  )}
                                  {stop.actual_time && (
                                    <p style={{ margin: "4px 0", fontSize: "12px" }}>
                                      <strong>Actual:</strong> {stop.actual_time}
                                    </p>
                                  )}
                                  <p style={{ margin: "4px 0", fontSize: "12px" }}>
                                    <strong>Sequence:</strong> {stop.seq || index + 1}
                                  </p>
                                </div>
                              </Popup>
                            </Marker>
                          );
                        })} */}

                          {/* Markers for stopPinLocation (from getStopLocationsApi) */}
                          <MarkerClusterGroup
                            iconCreateFunction={(cluster) => {
                              const markers = cluster.getAllChildMarkers();
                              const firstMarker = markers[0];

                              return L.divIcon({
                                html: `
        <div class="cluster-wrapper" >
          <img src="${stopPinImg}" class="cluster-icon" />
               <span class="cluster-count">
                ... ${markers.length}
              </span>
        </div>
      `,
                                className: "custom-cluster",
                                iconSize: L.point(50, 50),
                              });
                            }}
                          >
                            {stopPinLocation.map((coord, index) => {
                              return (
                                <Marker key={`Stop${index}`} position={[coord.latitude, coord.longitude]} icon={createCustomMarker(index + 1)}>
                                  <Popup>{sanitizeInput(coord.stop_code || "")}</Popup>
                                </Marker>
                              );
                            })}
                          </MarkerClusterGroup>
                          <MarkerClusterGroup
                            iconCreateFunction={(cluster) => {
                              const markers = cluster.getAllChildMarkers();
                              const firstMarker = markers[0];

                              return L.divIcon({
                                html: `
        <div style="position: relative; display: inline-block;" >
          <img src="${completedPinImg}" style="width:40px;height:40px;object-fit:cover;" />

               <span style="
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                color: white;
                font-size: 13px;
                font-weight: bold;
              ">
               ... ${markers.length}
              </span>
        </div>
      `,
                                className: "custom-cluster",
                                iconSize: L.point(50, 50),
                              });
                            }}
                          >
                            {/* Markers for delivery pins */}
                            {deliveryPinLocation
                              .filter((marker) => marker["stop_code"] != null)
                              .map((coord, index) => (
                                <Marker key={`delivery-${index}`} position={[coord.latitude, coord.longitude]} icon={createCompletedPinMarker(index + 1)}>
                                  <Popup>
                                    <div className="map-popup-container">
                                      <p className="map-popup-text">
                                        <strong>Trip:</strong> {coord.trip_id || "N/A"}
                                      </p>
                                      <p className="map-popup-text">
                                        <strong>Stop:</strong> {coord.stop_code || "N/A"}
                                      </p>
                                      <p className="map-popup-text">
                                        <strong>Status:</strong> {coord.status || "N/A"}
                                      </p>
                                    </div>
                                  </Popup>
                                </Marker>
                              ))}
                          </MarkerClusterGroup>
                          {/* Polylines */}
                          {polylinePlanned.map((poly, index) => (
                            <Polyline key={`${poly.id}`} positions={poly.positionList} color="#FDCA01" weight={5} />
                          ))}
                          {polylineHistory.map((poly, index) => (
                            <Polyline key={`${poly.id}`} positions={poly.positionList} color="#3469E7" weight={4} />
                          ))}

                          {/* Polylines */}
                          {/* <Polyline positions={route1} color="blue" weight={4} />
                         <Polyline positions={route2} color="red" weight={4} dashArray="5,10" /> */}
                        </MapContainer>
                      </div>
                    )}
                  </div>
                </div>

                <div className="stops-panel">
                  <div className="bg-white rounded-4 stops-panel-inner">
                    {trip?.next_stop && (
                      <div className="rounded-4 p-3 mb-3 stop-card-container next-stop">
                        <div className="bg-white rounded-3 p-2 d-flex align-items-center justify-content-between stop-card-header">
                          <div className="d-flex align-items-center justify-content-start">
                            <div className="d-flex align-items-center justify-content-center px-3 stop-icon-wrapper">
                              <img src={stopIcon} alt="Stop Icon" />
                            </div>
                            <div className="time-text-value ms-2">#{trip?.next_stop?.code ?? ""}</div>
                          </div>

                          <div className="d-flex align-items-center justify-content-end">
                            <div className="d-flex align-items-center justify-content-center rounded-2 px-2 fs-11 fw-500 next-stop-badge">
                              Next Stop
                            </div>
                            <div
                              onClick={() => { }}
                              className="ms-2 d-flex align-items-center justify-content-center action-icon-wrapper"
                            >
                              <img src={receiverIcon} alt="Receiver Icon" />
                            </div>
                          </div>
                        </div>
                        <div className="w-100 d-flex align-items-center justify-content-between mt-3">
                          <div className="d-flex align-items-center gap-2" style={{ flex: 1 }}>
                            <div className="stop-avatar-wrapper"
                              style={{
                                cursor: trip?.next_stop?.profile && trip?.next_stop?.profile !== "" ? "pointer" : "default",
                              }}
                              onClick={() => {
                                if (trip?.next_stop?.profile && trip?.next_stop?.profile !== "") {
                                  setSelectedProfile(trip.next_stop.profile);
                                  setShowProfilePopup(true);
                                }
                              }}
                            >
                              {trip?.next_stop?.profile && trip?.next_stop?.profile !== "" ? (
                                <img
                                  src={trip.next_stop.profile}
                                  alt="profile"
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                  }}
                                />
                              ) : (
                                trip?.next_stop?.customer?.charAt(0) ?? "C"
                              )}
                            </div>
                            <div>
                              <div className="trip-batch-driver break-word">{sanitizeInput(trip?.license_plate || "")}</div>
                              <div className="mt-1 trip-batch-vehicle">{sanitizeInput(trip?.next_stop?.ph_number || "")}</div>
                            </div>
                          </div>

                          <div className="d-flex gap-2" style={{ width: "86px" }}>
                            {trip?.next_stop?.ph_number && (
                              <div
                                onClick={() => {
                                  const ph = trip?.next_stop?.ph_number;
                                  if (ph) window.location.href = `tel:${sanitizeInput(ph)}`;
                                }}
                                className="d-flex align-items-center justify-content-center action-icon-wrapper"
                              >
                                <img src={receiverIcon} alt="Receiver Icon" />
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="mt-2 d-flex align-items-center justify-content-start">
                          <div className="d-flex align-items-center justify-content-center px-3 action-icon-wrapper">
                            <img src={locationIcon} alt="Location Icon" />
                          </div>
                          <div className="location-address ms-2">{sanitizeInput(trip?.next_stop?.address || "")}</div>
                        </div>
                      </div>
                    )}

                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div className="d-flex gap-2 align-items-center">
                        <button className={`btn btn-sm ${stopsFilter === "All" ? "selected-btn selected-stop-btn" : "deselect-btn deselect-stop-btn"}`} onClick={() => setStopsFilter("All")}>
                          All
                        </button>
                        <button className={`btn btn-sm ${stopsFilter === "true" ? "selected-btn selected-stop-btn" : "deselect-btn deselect-stop-btn"}`} onClick={() => setStopsFilter("true")}>
                          Completed
                        </button>
                        <button className={`btn btn-sm ${stopsFilter === "false" ? "selected-btn selected-stop-btn" : "deselect-btn deselect-stop-btn"}`} onClick={() => setStopsFilter("false")}>
                          Pending
                        </button>
                      </div>
                    </div>

                    <div className="mb-3">
                      <input className="form-control form-control-sm" placeholder="Search stops" value={query} onChange={(e) => setQuery(e.target.value)} />
                    </div>

                    {isLoadingStops ? (
                      <>
                        {/* Skeleton for stop cards while filtering/searching */}
                        {Array.from({ length: 3 }).map((_, idx) => (
                          <div key={idx} className="rounded-4 p-3 mb-3" style={{ background: "#F8F8F8" }}>
                            <div className="bg-white rounded-3 p-2 d-flex align-items-center justify-content-between mb-3">
                              <div className="d-flex align-items-center gap-2">
                                <div className="skeleton" style={{ width: "28px", height: "28px", borderRadius: "50%" }} />
                                <div className="skeleton skeleton-text" style={{ width: "80px" }} />
                              </div>
                              <div className="skeleton skeleton-badge" />
                            </div>
                            <div className="d-flex align-items-center gap-2 mb-3">
                              <div className="skeleton" style={{ width: "36px", height: "36px", borderRadius: "8px" }} />
                              <div className="flex-grow-1">
                                <div className="skeleton skeleton-text-sm mb-2" style={{ width: "100px" }} />
                                <div className="skeleton skeleton-text-sm" style={{ width: "80px" }} />
                              </div>
                            </div>
                            <div className="d-flex align-items-center gap-2 mb-3">
                              <div className="skeleton" style={{ width: "36px", height: "36px", borderRadius: "50%" }} />
                              <div className="skeleton skeleton-text-sm flex-grow-1" />
                            </div>
                            <div className="bg-white rounded-3 d-flex gap-2 p-2">
                              <div className="skeleton skeleton-text-sm flex-grow-1" />
                              <div className="skeleton skeleton-text-sm flex-grow-1" />
                              <div className="skeleton skeleton-text-sm" style={{ width: "40px" }} />
                            </div>
                          </div>
                        ))}
                      </>
                    ) : (
                      <>
                        {stopList.map((stop, index) => (
                          <div key={`stop_${index}`} className="mb-3">
                            <div className="rounded-4 p-3 mb-3 stop-card-container regular-stop">
                              <div className="bg-white rounded-3 p-2 d-flex align-items-center justify-content-between stop-card-header">
                                <div className="d-flex align-items-center justify-content-start">
                                  <div className="d-flex align-items-center justify-content-center px-3 stop-icon-wrapper">
                                    <img src={stopIcon} alt="Stop Icon" />
                                  </div>
                                  <div className="time-text-value ms-2">#{stop.stop_code ?? "---"}</div>
                                </div>

                                <div className="d-flex align-items-center justify-content-end">
                                  {stop.status === true && (
                                    <div
                                      className="d-flex align-items-center justify-content-center rounded-2 px-2 fs-11 fw-500"
                                      style={{
                                        background: "#D2FFE4",
                                        height: "26px",
                                        color: "#2C8C53",
                                      }}
                                    >
                                      Completed
                                    </div>
                                  )}

                                  {stop.status === false && (
                                    <div
                                      className="d-flex align-items-center justify-content-center rounded-2 px-2 fs-11 fw-500"
                                      style={{
                                        background: "#FFF9E3",
                                        height: "26px",
                                        color: "#FFC05D",
                                      }}
                                    >
                                      Pending
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="w-100 d-flex align-items-center justify-content-between mt-3">
                                <div className="d-flex align-items-center gap-2" style={{ flex: 1 }}>
                                  <div className="stop-avatar-wrapper"
                                    style={{
                                      cursor: stop?.profile && stop?.profile !== "" ? "pointer" : "default",
                                    }}
                                    onClick={() => {
                                      if (stop?.profile && stop?.profile !== "") {
                                        setSelectedProfile(stop.profile);
                                        setShowProfilePopup(true);
                                      }
                                    }}
                                  >
                                    {stop?.profile && stop?.profile !== "" ? (
                                      <img
                                        src={stop.profile}
                                        alt="profile"
                                        style={{
                                          width: "100%",
                                          height: "100%",
                                          objectFit: "cover",
                                        }}
                                      />
                                    ) : (
                                      stop?.customer?.charAt(0) ?? "C"
                                    )}
                                  </div>
                                  <div>
                                    <div className="trip-batch-driver break-word">{sanitizeInput(stop.customer || "")}</div>
                                    <div className="mt-1 trip-batch-vehicle">{sanitizeInput(stop.ph_number || "")}</div>
                                  </div>
                                </div>

                                <div className="d-flex gap-2" style={{ width: "86px" }}>
                                  {trip?.driverDetails?.length > 0 && (
                                    <div
                                      onClick={() => {
                                        if (stop.ph_number) window.location.href = `tel:${sanitizeInput(stop.ph_number)}`;
                                      }}
                                      className="d-flex align-items-center justify-content-center action-icon-wrapper"
                                    >
                                      <img src={receiverIcon} alt="Receiver Icon" />
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="mt-2 d-flex align-items-center justify-content-start">
                                <div className="d-flex align-items-center justify-content-center px-3 action-icon-wrapper">
                                  <img src={locationIcon} alt="Location Icon" />
                                </div>
                                <div className="location-address ms-2">{sanitizeInput(stop.address || "")}</div>
                              </div>
                              <div className="mt-2 bg-white rounded-3 d-flex align-items-center justify-content-between delivery-time-card">
                                <div className="ms-2 d-flex flex-column align-items-start py-1">
                                  <div className="fs-11 fw-400 text-gray">Planned Delivery Time </div>
                                  <div className="fs-11 fw-500 textcolor">{sanitizeInput(stop.planned_time || "")}</div>
                                </div>

                                <div className="px-2 d-flex flex-column align-items-start  py-1 actual-time-wrapper">
                                  <div className="fs-11 fw-400 text-gray">Actual Delivery Time </div>
                                  <div className="fs-11 fw-500 textcolor">{sanitizeInput(stop.actual_time || "")}</div>
                                </div>
                                <div className="me-2 d-flex flex-column align-items-start py-1">
                                  <div className="fs-11 fw-400 text-gray">Seq. </div>
                                  <div className="fs-11 fw-500 textcolor">{sanitizeNumericInput(stop.seq || index + 1)} </div>
                                </div>
                              </div>

                            </div>
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      <ToastContainer position="top-end" className="p-3 toast-container-custom">
        <Toast onClose={() => setToast({ ...toast, show: false })} show={toast.show} delay={3000} autohide bg={toast.variant}>
          <Toast.Body className="text-white">{toast.message}</Toast.Body>
        </Toast>
      </ToastContainer>

      {/* Profile Image Modal */}
      <ProfileImageModal show={showProfilePopup} onClose={() => setShowProfilePopup(false)} imageUrl={selectedProfile} />
    </>
  );
};
